import "reactflow/dist/style.css";
import "../Flow/index.css";
import "./analytics.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ReactFlow, {
	useNodesState,
	useEdgesState,
	ReactFlowProvider,
	Controls,
	ControlButton,
	MiniMap,
	Background,
	MarkerType,
} from "reactflow";
import { FaArrowLeft, FaMoon, FaSun } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";

import Condition from "../Nodes/Condition";
import Start from "../Nodes/Start";
import SendMessage from "../Nodes/SendMessage";
import Webhook from "../Nodes/Webhook";
import Question from "../Nodes/Question";
import AiAgent from "../Nodes/AiAgent";
import CreateUpdateContact from "../Nodes/CreateUpdateContact";
import End from "../Nodes/End";
import Gush from "../Nodes/Gush";
import Jump from "../Nodes/Jump";
import SetVar from "../Nodes/SetVar";
import Sleep from "../Nodes/Sleep";
import CreateTask from "../Nodes/CreateTask";
import CreateDeal from "../Nodes/CreateDeal";
import CreateTicket from "../Nodes/CreateTicket";
import SetTag from "../Nodes/SetTag";
import Comment from "../Nodes/Comment";
import OutputBalancer from "../Nodes/OutputBalancer";
import { serverApi } from "../../services/api";
import { buildBuilderQueryString } from "../../helpers/build-builder-query";
import AnalyticsEdge from "./AnalyticsEdge";
import DateRangeFilter from "./DateRangeFilter";
import NodeSearch from "../Flow/NodeSearch";
import { withAnalyticsStopBadge } from "./withAnalyticsStopBadge";

const nodeTypes = {
	conditional: withAnalyticsStopBadge(Condition),
	start: withAnalyticsStopBadge(Start),
	message: withAnalyticsStopBadge(SendMessage),
	webhook: withAnalyticsStopBadge(Webhook),
	aiagent: withAnalyticsStopBadge(AiAgent),
	question: withAnalyticsStopBadge(Question),
	createupdatecontact: withAnalyticsStopBadge(CreateUpdateContact),
	end: End,
	gush: Gush,
	jump: withAnalyticsStopBadge(Jump),
	setvar: withAnalyticsStopBadge(SetVar),
	settag: withAnalyticsStopBadge(SetTag),
	sleep: withAnalyticsStopBadge(Sleep),
	createtask: withAnalyticsStopBadge(CreateTask),
	createdeal: withAnalyticsStopBadge(CreateDeal),
	createticket: withAnalyticsStopBadge(CreateTicket),
	createnote: Comment,
	outputbalancer: withAnalyticsStopBadge(OutputBalancer),
};

const edgeTypes = {
	buttonedge: AnalyticsEdge,
};

const noop = () => {};

/** Mesmo intervalo padrão do DateRangeFilter (últimos 7 dias, fim = hoje; dados só a partir de 2026-01-01). */
function getDefaultDateRangeISO() {
	const dataMin = new Date(2026, 0, 1, 0, 0, 0, 0);
	const end = new Date();
	end.setHours(23, 59, 59, 999);
	const start = new Date();
	start.setDate(start.getDate() - 6);
	start.setHours(0, 0, 0, 0);
	const clampedStart = start < dataMin ? dataMin : start;
	return { startDate: clampedStart.toISOString(), endDate: end.toISOString() };
}

const EMPTY_ANALYTICS = {
	totalContacts: 0,
	nodeStats: {},
	edgeStats: {},
	lastStopCounts: {},
};

function mergeAnalyticsIntoNodes(
	nodes,
	analytics,
	{ analyticsLoaded = true, imputeStart = true } = {}
) {
	const totalContacts = analytics?.totalContacts ?? 0;
	const nodeStats = analytics?.nodeStats ?? {};
	const lastStopCounts = analytics?.lastStopCounts ?? {};
	return nodes.map((node) => {
		const ref = node.id;
		let reached = nodeStats[ref] || 0;
		// O tracking costuma começar após o Start; sem imputação, "chegaram" fica 0 com conversas no período.
		if (
			imputeStart &&
			node.type === "start" &&
			reached === 0 &&
			totalContacts > 0
		) {
			reached = totalContacts;
		}
		const stopped = lastStopCounts[ref] || 0;
		const stoppedPct = reached > 0 ? (stopped / reached) * 100 : 0;
		return {
			...node,
			data: {
				...node.data,
				analyticsLoaded,
				analyticsStoppedPct: stoppedPct,
				analyticsStoppedCount: stopped,
				analyticsReachedCount: reached,
				analyticsTotalContacts: totalContacts,
			},
		};
	});
}

function resolveEdgeKey(edge, edgeStats) {
	if (edge.sourceHandle) {
		const handleKey = `${edge.source}::${edge.sourceHandle}::${edge.target}`;
		if (edgeStats[handleKey] !== undefined) return handleKey;
	}
	return `${edge.source}::${edge.target}`;
}

function resolveEdgeCount(edge, edgeStats) {
	const simpleKey = `${edge.source}::${edge.target}`;
	if (edge.sourceHandle === "web-success") {
		const handleKey = `${edge.source}::web-success::${edge.target}`;
		return (edgeStats[handleKey] || 0) + (edgeStats[simpleKey] || 0);
	}
	// Antes das duas saídas, o nó de Flows tinha só a saída que virou "Padrão".
	if (edge.sourceHandle === "flow-default") {
		const handleKey = `${edge.source}::flow-default::${edge.target}`;
		return (edgeStats[handleKey] || 0) + (edgeStats[simpleKey] || 0);
	}
	if (edge.sourceHandle === "web-error") {
		const handleKey = `${edge.source}::web-error::${edge.target}`;
		return edgeStats[handleKey] || 0;
	}
	const key = resolveEdgeKey(edge, edgeStats);
	return edgeStats[key] || 0;
}

function mergeAnalyticsIntoEdges(
	edges,
	analytics,
	{ analyticsLoaded = true, startNodeRefs = new Set(), imputeStart = true } = {}
) {
	const totalContacts = analytics?.totalContacts ?? 0;
	const nodeStats = analytics?.nodeStats ?? {};
	const edgeStats = analytics?.edgeStats ?? {};

	// Soma de transições por nó de origem.
	// Coleta todas as chaves relevantes do edgeStats (com e sem handle) para cada source.
	const keysPerSource = {};
	for (const e of edges) {
		if (!keysPerSource[e.source]) keysPerSource[e.source] = new Set();
		if (e.sourceHandle) {
			const handleKey = `${e.source}::${e.sourceHandle}::${e.target}`;
			keysPerSource[e.source].add(handleKey);
		}
		const simpleKey = `${e.source}::${e.target}`;
		keysPerSource[e.source].add(simpleKey);
	}
	const outgoingTotalBySource = {};
	for (const src of Object.keys(keysPerSource)) {
		let sum = 0;
		for (const k of keysPerSource[src]) {
			sum += edgeStats[k] || 0;
		}
		outgoingTotalBySource[src] = sum;
	}

	// Saída única do Start: a API muitas vezes não grava transição start→próximo bloco; com conversas no período, 100% por aqui.
	if (imputeStart) {
		for (const startId of startNodeRefs) {
			const outList = edges.filter((e) => e.source === startId);
			const sum = outgoingTotalBySource[startId] || 0;
			if (sum === 0 && totalContacts > 0 && outList.length === 1) {
				outgoingTotalBySource[startId] = totalContacts;
			}
		}
	}

	return edges.map((edge) => {
		let edgeCount = resolveEdgeCount(edge, edgeStats);
		let sourceNodeCount = nodeStats[edge.source] || 0;
		const outgoingTotal = outgoingTotalBySource[edge.source] || 0;

		const outsFromSource = edges.filter((e) => e.source === edge.source);
		if (
			imputeStart &&
			startNodeRefs.has(edge.source) &&
			edgeCount === 0 &&
			totalContacts > 0 &&
			outsFromSource.length === 1
		) {
			edgeCount = totalContacts;
		}
		if (
			imputeStart &&
			startNodeRefs.has(edge.source) &&
			sourceNodeCount === 0 &&
			totalContacts > 0 &&
			outsFromSource.length === 1
		) {
			sourceNodeCount = totalContacts;
		}

		const useArrivalsDenominator =
			sourceNodeCount > 0 && outgoingTotal <= sourceNodeCount;
		const percentageAmongExits =
			outgoingTotal > 0 ? (edgeCount / outgoingTotal) * 100 : 0;
		const percentageFromSource = useArrivalsDenominator
			? (edgeCount / sourceNodeCount) * 100
			: percentageAmongExits;
		const percentageFromTotal =
			totalContacts > 0 ? (edgeCount / totalContacts) * 100 : 0;
		return {
			...edge,
			data: {
				...edge.data,
				edgeCount,
				sourceNodeCount,
				outgoingTotal,
				totalContacts,
				percentageFromSource,
				percentageAmongExits,
				analyticsEdgePctBasis: useArrivalsDenominator ? "arrivals" : "exits",
				percentageFromTotal,
				analyticsLoaded,
			},
		};
	});
}

const FLOW_NODE_TYPES = new Set([
	"conditional",
	"start",
	"message",
	"webhook",
	"aiagent",
	"createupdatecontact",
	"question",
	"end",
	"gush",
	"jump",
	"setvar",
	"settag",
	"sleep",
	"createtask",
	"createdeal",
	"createticket",
	"createnote",
	"outputbalancer",
]);

function canvasFromApi(data, { botId, operators, chattags, agentsData }) {
	const nodesArr = (data.nodes || []).map((node) => {
		let nodeData = { ...node };
		if (node.type === "aiagent" && node.aiAgentId && !node.aiAgentName) {
			const foundAgent = agentsData.find(
				(a) => a?.id === node.aiAgentId || a?._id === node.aiAgentId
			);
			if (foundAgent) {
				nodeData.aiAgentName = foundAgent.name;
			}
		}

		return {
			id: node.ref,
			...(FLOW_NODE_TYPES.has(node.type) ? { type: node.type } : {}),
			sourcePosition: "right",
			targetPosition: "left",
			data: {
				...nodeData,
				botId,
				hideNodeActions: true,
				deleteNodeById: noop,
				openModalEdit: noop,
				currentNode: node,
				setNodes: noop,
				operators,
				chattags,
				agents: agentsData,
			},
			position: node.position,
		};
	});

	// Blindagem contra edges duplicados do backend (ver Flow.js getNodes): o
	// mesmo bot pode trazer a mesma conexão repetida com id igual, o que colide
	// na key do React. Mantemos uma conexão por tupla e id único por edge.
	const edgeSeen = new Set();
	const edgeIds = new Set();
	const edgesArr = (data.edges || []).reduce((acc, edge) => {
		const sh = edge.sourceHandle || "";
		const th = edge.targetHandle || "";
		const connKey = `${edge.source}|${sh}|${edge.target}|${th}`;
		if (edgeSeen.has(connKey)) return acc;
		edgeSeen.add(connKey);

		let id = edge.id;
		if (!id || edgeIds.has(id)) {
			id = `${edge.source}-${sh}-${edge.target}-${th}-${acc.length}`;
		}
		edgeIds.add(id);

		acc.push({
			id,
			source: edge.source,
			target: edge.target,
			type: "buttonedge",
			sourceHandle: edge.sourceHandle || undefined,
			markerEnd: {
				type: MarkerType.ArrowClosed,
			},
			data: {},
		});
		return acc;
	}, []);

	return { nodesArr, edgesArr };
}

export default function AnalyticsFlow() {
	const { botId, chatId } = useParams();
	const [searchParams] = useSearchParams();
	const builderQuery = buildBuilderQueryString(searchParams);

	const baseEdgesRef = useRef([]);
	/** Refs dos nós `type === "start"` (imputação de % quando a API não grava aresta start→…) */
	const startNodeRefsRef = useRef(new Set());
	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);
	const [botName, setBotName] = useState("");
	const [canvasDark, setCanvasDark] = useState(() => {
		try {
			return localStorage.getItem("builderCanvasDark") === "1";
		} catch {
			return false;
		}
	});
	const toggleCanvasTheme = () => {
		setCanvasDark((dark) => {
			const next = !dark;
			try {
				localStorage.setItem("builderCanvasDark", next ? "1" : "0");
			} catch {}
			return next;
		});
	};
	const [reactFlowInstance, setReactFlowInstance] = useState(null);
	const [loadingBot, setLoadingBot] = useState(true);
	const [loadingAnalytics, setLoadingAnalytics] = useState(false);
	const [baseEdgesReady, setBaseEdgesReady] = useState(false);
	const [dateRange, setDateRange] = useState(getDefaultDateRangeISO);
	const [conversationSummary, setConversationSummary] = useState({
		total: 0,
		ready: false,
	});
	const [loadError, setLoadError] = useState(false);

	const getOperators = useCallback(async () => {
		try {
			const resp = await serverApi.get(`/operators/${botId}`);
			return resp.data;
		} catch (e) {
			console.log(e);
			return [];
		}
	}, [botId]);

	const getChatTags = useCallback(async () => {
		try {
			const resp = await serverApi.get(`/tags/${botId}`);
			return resp.data;
		} catch (e) {
			console.log(e);
			return [];
		}
	}, [botId]);

	const getAgents = useCallback(async () => {
		try {
			const resp = await serverApi.get(`/bot/ai/agents/${botId}`);
			return resp.data || [];
		} catch (e) {
			console.log(e);
			return [];
		}
	}, [botId]);

	const loadBot = useCallback(async () => {
		setLoadingBot(true);
		setBaseEdgesReady(false);
		setLoadError(false);
		setConversationSummary({ total: 0, ready: false });
		try {
			const operators = await getOperators();
			const chattags = await getChatTags();
			const agentsData = await getAgents();
			const extras = { botId, operators, chattags, agentsData };

			if (chatId) {
				const { data } = await serverApi.get(`/chat/analytics/${chatId}`);
				const { nodesArr, edgesArr } = canvasFromApi(data, extras);
				const payload = {
					totalContacts: data?.totalContacts ?? 0,
					nodeStats: data?.nodeStats ?? {},
					edgeStats: data?.edgeStats ?? {},
					lastStopCounts: data?.lastStopCounts ?? {},
				};
				baseEdgesRef.current = edgesArr;
				startNodeRefsRef.current = new Set();
				setNodes(
					mergeAnalyticsIntoNodes(nodesArr, payload, {
						analyticsLoaded: true,
						imputeStart: false,
					})
				);
				setEdges(
					mergeAnalyticsIntoEdges(edgesArr, payload, {
						analyticsLoaded: true,
						startNodeRefs: startNodeRefsRef.current,
						imputeStart: false,
					})
				);
				setBotName(data?.name || "");
				setBaseEdgesReady(true);
				return;
			}

			const { data } = await serverApi.get(`/bot/${botId}`);
			const { nodesArr, edgesArr } = canvasFromApi(data, extras);

			baseEdgesRef.current = edgesArr;
			startNodeRefsRef.current = new Set(
				nodesArr.filter((n) => n.type === "start").map((n) => n.id)
			);
			setNodes(
				mergeAnalyticsIntoNodes(nodesArr, EMPTY_ANALYTICS, {
					analyticsLoaded: false,
				})
			);
			setEdges(
				mergeAnalyticsIntoEdges(edgesArr, EMPTY_ANALYTICS, {
					analyticsLoaded: false,
					startNodeRefs: startNodeRefsRef.current,
				})
			);
			setBotName(data.name);
			setBaseEdgesReady(true);
		} catch (e) {
			console.error(e);
			setLoadError(true);
			toast.error(chatId ? "Erro ao carregar o percurso" : "Erro ao carregar o bot");
		} finally {
			setLoadingBot(false);
		}
	}, [botId, chatId, getAgents, getChatTags, getOperators, setNodes, setEdges]);

	const fetchAnalytics = useCallback(
		async (range) => {
			if (!baseEdgesReady || !range?.startDate || !range?.endDate) return;
			setLoadingAnalytics(true);
			let payload = EMPTY_ANALYTICS;
			try {
				const { data } = await serverApi.get(`/bot/analytics/${botId}`, {
					params: {
						startDate: range.startDate,
						endDate: range.endDate,
					},
				});
				payload = {
					totalContacts: data?.totalContacts ?? 0,
					nodeStats: data?.nodeStats ?? {},
					edgeStats: data?.edgeStats ?? {},
					lastStopCounts: data?.lastStopCounts ?? {},
				};
			} catch (e) {
				console.error(e);
				toast.error("Erro ao carregar analytics");
			} finally {
				setConversationSummary({
					total: payload.totalContacts ?? 0,
					ready: true,
				});
				setEdges(
					mergeAnalyticsIntoEdges(baseEdgesRef.current, payload, {
						analyticsLoaded: true,
						startNodeRefs: startNodeRefsRef.current,
					})
				);
				setNodes((prev) =>
					mergeAnalyticsIntoNodes(prev, payload, { analyticsLoaded: true })
				);
				setLoadingAnalytics(false);
			}
		},
		[botId, baseEdgesReady, setEdges, setNodes]
	);

	const handleRangeChange = useCallback((range) => {
		setDateRange(range);
	}, []);

	useEffect(() => {
		loadBot();
	}, [loadBot]);

	useEffect(() => {
		if (chatId) return;
		if (!baseEdgesReady) return;
		fetchAnalytics(dateRange);
	}, [chatId, baseEdgesReady, dateRange, fetchAnalytics]);

	if (loadingBot && nodes.length === 0) {
		return (
			<div
				className={`analytics-loading${
					canvasDark ? " analytics-loading--dark" : ""
				}`}
			>
				<div className="analytics-loading__box">
					<span className="analytics-loading__spinner" aria-hidden />
					<span>
						{chatId
							? "Carregando percurso da conversa…"
							: "Carregando fluxo do bot…"}
					</span>
				</div>
			</div>
		);
	}

	if (!loadingBot && nodes.length === 0) {
		return (
			<div
				className={`analytics-loading${
					canvasDark ? " analytics-loading--dark" : ""
				}`}
			>
				<div className="analytics-loading__box">
					{chatId && !loadError
						? "Esta conversa não tem percurso registrado."
						: "Não foi possível carregar o fluxo deste bot."}
				</div>
			</div>
		);
	}

	const focusNodeById = (node) => {
		if (!node) return;
		const width = node.width || 170;
		const height = node.height || 40;
		const cx = (node.position?.x || 0) + width / 2;
		const cy = (node.position?.y || 0) + height / 2;
		if (reactFlowInstance?.setCenter) {
			reactFlowInstance.setCenter(cx, cy, { zoom: 1.4, duration: 800 });
		}
		const el = document.querySelector(
			`.react-flow__node[data-id="${node.id}"]`
		);
		if (el) {
			el.classList.remove("rv-node-pulse");
			void el.offsetWidth;
			el.classList.add("rv-node-pulse");
			setTimeout(() => el.classList.remove("rv-node-pulse"), 8000);
		}
	};

	return (
		<>
			<ToastContainer />
			<div
				className="dndflow"
				style={{ width: "100vw", height: "100vh", position: "relative" }}
			>
				<Link
					className="analytics-toolbar__back"
					to={`/builder/${botId}${builderQuery}`}
					style={{ position: "fixed", top: 12, left: 12, zIndex: 1002 }}
				>
					<FaArrowLeft aria-hidden />
					Voltar ao builder
				</Link>

				{!loadingBot && !chatId ? (
					<DateRangeFilter
						onRangeChange={handleRangeChange}
						conversationCount={conversationSummary.total}
						conversationsReady={conversationSummary.ready}
						conversationsRefreshing={
							loadingAnalytics && conversationSummary.ready
						}
					/>
				) : null}

				{loadingAnalytics ? (
					<div
						style={{
							position: "fixed",
							top: 56,
							right: 16,
							zIndex: 1002,
							fontSize: 13,
							color: "#94a3b8",
						}}
					>
						Atualizando dados…
					</div>
				) : null}

				<span className="analytics-flow-title">
					{chatId ? "Percurso" : "Analytics"} · {botName}
				</span>

				<NodeSearch
					nodes={nodes}
					onSelect={focusNodeById}
					className="node-search--analytics"
				/>

				<ReactFlowProvider>
					<div
						className="reactflow-wrapper"
						style={{ width: "100%", height: "100%" }}
					>
						<ReactFlow
							className="analytics-flow-canvas"
							nodes={nodes}
							edges={edges}
							onInit={setReactFlowInstance}
							onNodesChange={onNodesChange}
							onEdgesChange={onEdgesChange}
							nodesDraggable={true}
							nodesConnectable={false}
							elementsSelectable={false}
							panOnDrag
							zoomOnScroll
							zoomOnPinch
							fitView
							attributionPosition="bottom-left"
							nodeTypes={nodeTypes}
							edgeTypes={edgeTypes}
							deleteKeyCode={null}
							multiSelectionKeyCode={null}
						>
							<Background
								variant="dots"
								gap={22}
								size={2.2}
								color={canvasDark ? "#565c7d" : "#c4c5cf"}
								style={{
									backgroundColor: canvasDark ? "#454b6b" : "#eef0f3",
								}}
							/>
							<MiniMap
								nodeColor={"#c9c9cc"}
								maskColor={"rgba(18,18,18,0.08)"}
								nodeStrokeWidth={3}
								pannable
								zoomable
								zoomStep={1}
								ariaLabel={`MiniMap analytics ${botName}`}
							/>
							<Controls showInteractive={false}>
								<ControlButton
									onClick={toggleCanvasTheme}
									title={canvasDark ? "Fundo claro" : "Fundo escuro"}
								>
									{canvasDark ? <FaSun /> : <FaMoon />}
								</ControlButton>
							</Controls>
						</ReactFlow>
					</div>
				</ReactFlowProvider>
			</div>
		</>
	);
}
